import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  Divider,
  CircularProgress,
  Tooltip,
  Tab,
  Tabs,
} from '@mui/material';
import {
  Add,
  Phone,
  Email,
  Event,
  Task,
  Refresh,
  CheckCircle,
  Schedule,
  Warning,
  Delete,
  Edit,
  Today,
  CalendarMonth,
  Flag,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { activityAPI } from '../../services/api';
import { toast } from 'react-toastify';

const activityTypeIcons = {
  call: <Phone />,
  email: <Email />,
  meeting: <Event />,
  task: <Task />,
  follow_up: <Refresh />,
  deadline: <Warning />,
  reminder: <Schedule />,
};

const priorityColors = {
  low: '#10b981',
  medium: '#f59e0b',
  high: '#f97316',
  urgent: '#ef4444',
};

const Activities = () => {
  const [activities, setActivities] = useState([]);
  const [todayActivities, setTodayActivities] = useState([]);
  const [overdueActivities, setOverdueActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    type: 'task',
    description: '',
    dueDate: new Date(),
    dueTime: '',
    priority: 'medium',
  });

  const fetchActivities = async () => {
    setLoading(true);
    try {
      const [allRes, todayRes, overdueRes, statsRes] = await Promise.all([
        activityAPI.getAll({ status: 'pending' }),
        activityAPI.getToday(),
        activityAPI.getOverdue(),
        activityAPI.getStats(),
      ]);
      setActivities(allRes.data.activities);
      setTodayActivities(todayRes.data);
      setOverdueActivities(overdueRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivities();
  }, []);

  const handleCreateActivity = async () => {
    if (!formData.title || !formData.dueDate) {
      toast.error('Please fill in required fields');
      return;
    }
    try {
      if (editingActivity) {
        await activityAPI.update(editingActivity._id, formData);
        toast.success('Activity updated successfully');
      } else {
        await activityAPI.create(formData);
        toast.success('Activity created successfully');
      }
      setDialogOpen(false);
      setEditingActivity(null);
      setFormData({
        title: '',
        type: 'task',
        description: '',
        dueDate: new Date(),
        dueTime: '',
        priority: 'medium',
      });
      fetchActivities();
    } catch (error) {
      toast.error('Failed to save activity');
    }
  };

  const handleComplete = async (activity) => {
    try {
      await activityAPI.complete(activity._id);
      toast.success('Activity completed!');
      fetchActivities();
    } catch (error) {
      toast.error('Failed to complete activity');
    }
  };

  const handleDelete = async (id) => {
    try {
      await activityAPI.delete(id);
      toast.success('Activity deleted');
      fetchActivities();
    } catch (error) {
      toast.error('Failed to delete activity');
    }
  };

  const handleEdit = (activity) => {
    setEditingActivity(activity);
    setFormData({
      title: activity.title,
      type: activity.type,
      description: activity.description || '',
      dueDate: new Date(activity.dueDate),
      dueTime: activity.dueTime || '',
      priority: activity.priority,
    });
    setDialogOpen(true);
  };

  const getStatusCount = (status) => {
    const stat = stats?.statusBreakdown?.find(s => s._id === status);
    return stat?.count || 0;
  };

  const ActivityItem = ({ activity, showCheckbox = true }) => (
    <ListItem
      sx={{
        bgcolor: activity.status === 'overdue' ? 'rgba(239, 68, 68, 0.05)' : 'transparent',
        borderRadius: 2,
        mb: 1,
        border: '1px solid',
        borderColor: activity.status === 'overdue' ? 'rgba(239, 68, 68, 0.2)' : 'divider',
      }}
    >
      {showCheckbox && (
        <ListItemIcon>
          <Checkbox
            edge="start"
            checked={activity.status === 'completed'}
            onChange={() => handleComplete(activity)}
            disabled={activity.status === 'completed'}
          />
        </ListItemIcon>
      )}
      <ListItemIcon sx={{ minWidth: 40, color: priorityColors[activity.priority] }}>
        {activityTypeIcons[activity.type]}
      </ListItemIcon>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              fontWeight={600}
              sx={{ textDecoration: activity.status === 'completed' ? 'line-through' : 'none' }}
            >
              {activity.title}
            </Typography>
            <Chip
              label={activity.priority}
              size="small"
              sx={{
                bgcolor: `${priorityColors[activity.priority]}20`,
                color: priorityColors[activity.priority],
                fontWeight: 600,
                fontSize: '0.65rem',
              }}
            />
            {activity.status === 'overdue' && (
              <Chip label="Overdue" size="small" color="error" sx={{ fontSize: '0.65rem' }} />
            )}
          </Box>
        }
        secondary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Due: {new Date(activity.dueDate).toLocaleDateString()}
              {activity.dueTime && ` at ${activity.dueTime}`}
            </Typography>
            {activity.relatedTo?.customer && (
              <Typography variant="caption" color="primary">
                {activity.relatedTo.customer.name}
              </Typography>
            )}
          </Box>
        }
      />
      <ListItemSecondaryAction>
        <Tooltip title="Edit">
          <IconButton size="small" onClick={() => handleEdit(activity)}>
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" color="error" onClick={() => handleDelete(activity._id)}>
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
      </ListItemSecondaryAction>
    </ListItem>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ backgroundColor: '#f5f5f5', minHeight: 'calc(100vh - 64px)', px: { xs: 1, sm: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', sm: 'center' }, 
          gap: 2,
          mb: 3 
        }}>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ color: '#333', fontSize: { xs: '1.5rem', md: '2rem' } }}>
              Activities
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Schedule and manage your tasks and follow-ups
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setDialogOpen(true)}
            sx={{ 
              backgroundColor: '#875A7B',
              textTransform: 'none',
              fontWeight: 500,
              px: 3,
              py: 1,
              '&:hover': { backgroundColor: '#714B68' },
            }}
          >
            New Activity
          </Button>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#dbeafe' }}>
                    <Today sx={{ color: '#2563eb' }} />
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>{stats?.todayCount || 0}</Typography>
                    <Typography variant="body2" color="text.secondary">Due Today</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fee2e2' }}>
                    <Warning sx={{ color: '#dc2626' }} />
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>{stats?.overdueCount || 0}</Typography>
                    <Typography variant="body2" color="text.secondary">Overdue</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fef3c7' }}>
                    <Schedule sx={{ color: '#d97706' }} />
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>{getStatusCount('pending')}</Typography>
                    <Typography variant="body2" color="text.secondary">Pending</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#d1fae5' }}>
                    <CheckCircle sx={{ color: '#059669' }} />
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>{getStatusCount('completed')}</Typography>
                    <Typography variant="body2" color="text.secondary">Completed</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs and Activities */}
        <Paper sx={{ borderRadius: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(e, v) => setTabValue(v)}
            sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
          >
            <Tab label={`Today (${todayActivities.length})`} icon={<Today />} iconPosition="start" />
            <Tab label={`Overdue (${overdueActivities.length})`} icon={<Warning />} iconPosition="start" />
            <Tab label={`All Pending (${activities.length})`} icon={<CalendarMonth />} iconPosition="start" />
          </Tabs>

          <Box sx={{ p: 2 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {tabValue === 0 && (
                  <List>
                    {todayActivities.length === 0 ? (
                      <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                        No activities due today 🎉
                      </Typography>
                    ) : (
                      todayActivities.map((activity) => (
                        <ActivityItem key={activity._id} activity={activity} />
                      ))
                    )}
                  </List>
                )}
                {tabValue === 1 && (
                  <List>
                    {overdueActivities.length === 0 ? (
                      <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                        No overdue activities ✅
                      </Typography>
                    ) : (
                      overdueActivities.map((activity) => (
                        <ActivityItem key={activity._id} activity={activity} />
                      ))
                    )}
                  </List>
                )}
                {tabValue === 2 && (
                  <List>
                    {activities.length === 0 ? (
                      <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                        No pending activities
                      </Typography>
                    ) : (
                      activities.map((activity) => (
                        <ActivityItem key={activity._id} activity={activity} />
                      ))
                    )}
                  </List>
                )}
              </>
            )}
          </Box>
        </Paper>

        {/* Create/Edit Activity Dialog */}
        <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); setEditingActivity(null); }} maxWidth="sm" fullWidth>
          <DialogTitle>{editingActivity ? 'Edit Activity' : 'New Activity'}</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Title"
                fullWidth
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={formData.type}
                      label="Type"
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    >
                      <MenuItem value="call">📞 Call</MenuItem>
                      <MenuItem value="email">📧 Email</MenuItem>
                      <MenuItem value="meeting">📅 Meeting</MenuItem>
                      <MenuItem value="task">✅ Task</MenuItem>
                      <MenuItem value="follow_up">🔄 Follow Up</MenuItem>
                      <MenuItem value="deadline">⏰ Deadline</MenuItem>
                      <MenuItem value="reminder">🔔 Reminder</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={formData.priority}
                      label="Priority"
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    >
                      <MenuItem value="low">🟢 Low</MenuItem>
                      <MenuItem value="medium">🟡 Medium</MenuItem>
                      <MenuItem value="high">🟠 High</MenuItem>
                      <MenuItem value="urgent">🔴 Urgent</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <DatePicker
                    label="Due Date"
                    value={formData.dueDate}
                    onChange={(date) => setFormData({ ...formData, dueDate: date })}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Time (optional)"
                    type="time"
                    fullWidth
                    value={formData.dueTime}
                    onChange={(e) => setFormData({ ...formData, dueTime: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setDialogOpen(false); setEditingActivity(null); }}>Cancel</Button>
            <Button variant="contained" onClick={handleCreateActivity}>
              {editingActivity ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default Activities;
